import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';

import ListingCard from '../ListingCard/ListingCard';
import { AvatarSmall } from '../Avatar/Avatar';
import { useConfiguration } from '../../context/configurationContext';
import routeConfiguration from '../../routing/routeConfiguration';

import { createSlug } from '../../util/urlHelpers';
import { FormattedMessage } from '../../util/reactIntl';
import { FORMAT, GAME } from '../../util/enums';
import { createResourceLocatorString } from '../../util/routes';
import { isArrayLength, safeFind } from '../../util/genericHelpers';

import css from './BookingCalendar.module.css';

const BookingCalendar = ({ author, bookings, handleEventClick }) => {
  const { profile } = author?.attributes || {};
  const [filterFormat, setFilterFormat] = useState('');
  const [selectedGame, setSelectedGame] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showToday, setShowToday] = useState(false);

  const intl = useIntl();
  const config = useConfiguration();

  const uniqueGame = Array.from(new Set(bookings.map(event => event?.game)));
  const uniqueFormat = Array.from(new Set(bookings.map(event => event?.format)));
  const listingFields = config.listing.listingFields;

  // Extracting games options
  const gamesOptions = isArrayLength(listingFields)
    ? safeFind(listingFields, f => f?.key === GAME)?.enumOptions
    : null;

  const formatOptions = safeFind(listingFields, f => f?.key.includes(FORMAT))?.enumOptions;

  // Filter bookings based on selected game and format
  const filterBookings = () => {
    return bookings.filter(event => {
      const matchesFormat = filterFormat ? event.format === filterFormat : true;
      const matchesGame = selectedGame ? event.game === selectedGame : true;
      const isToday = showToday ? moment(event.start).isSame(moment(), 'day') : true;
      return matchesFormat && matchesGame && isToday;
    }).sort((a, b) => new Date(a.start) - new Date(b.start));
  };

  const filteredEvents = filterBookings();

  const createURLToListing = (listing) => {
    const routes = routeConfiguration();
    const { uuid: id } = listing.id;
    const slug = createSlug(listing.attributes.title);
    return createResourceLocatorString('ListingPage', routes, { id, slug }, {});
  };

  const CustomEvent = ({ event }) => (
    <div className={css.customEvent}>
      <div className={css.accordionContent}>
        <div className={css.buttonsWrapper}>
          <ListingCard
            bookingCalender={true}
            urlToListing={createURLToListing(event?.currentListing)}
            listing={event?.currentListing}
            intl={intl}
            isInCarousel={false}
            config={config}
          />
        </div>
      </div>
    </div>
  );

  const groupedEvents = filteredEvents.reduce((acc, tournamentEvent) => {
    const date = moment(tournamentEvent.start).format("dddd, D. MMM");
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(tournamentEvent);
    return acc;
  }, {});

  // Handle clicks outside the selected event
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${css.eventDetails}`)) {
        setSelectedEvent(null); // Reset selected event
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={css.bookingCalendarWrapper}>
      <div className={css.topWrapper}>
        <div className={css.avtarWrapper}>
          <AvatarSmall user={author} />
          <div>{profile?.displayName} | Events</div>
        </div>
        <div className={css.filterContainer}>
          <div>
            <label><FormattedMessage id="BookingCalendar.tradingCardGame" /></label>
            <select
              value={selectedGame}
              onChange={e => {
                setSelectedGame(e.target.value);
                setFilterFormat(''); // Reset format when game changes
              }}
              className={css.titleDropdown}

            >
              <option value="">
                {intl.formatMessage({ id: 'LandingSearchForm.tradingCardGameOption' })}
              </option>
              {gamesOptions?.filter(game => uniqueGame?.includes(game.option)).map((game, index) => (
                <option key={index} value={game.option}>{game.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label><FormattedMessage id="BookingCalendar.format" /></label>
            <select
              value={filterFormat}
              onChange={e => setFilterFormat(e.target.value)}
              className={css.titleDropdown}
              disabled={!selectedGame} // Disable if no game is selected
            >
              <option disabled value="">
                {intl.formatMessage({ id: 'LandingSearchForm.chooseAnOption' })}
              </option>
              {formatOptions
                ?.filter(format => selectedGame ? uniqueFormat.some(uFormat => uFormat.startsWith(selectedGame) && uFormat.includes(format.option)) // Checks if any format starts with the selected game
                  : uniqueFormat.includes(format.option))
                .map((format, index) => (
                  <option key={index} value={format.option}>{format.label}</option>
                ))}
            </select>
          </div>
        </div>
      </div>
      <div className={css.calender} key="calendar">
        {Object.entries(groupedEvents).map(([date, events]) => (
          <div key={date}>
            <h3 className={css.dateHeading}>{date}</h3>
            <div className={css.eventTable}>
              <div>
                {events.map((tournamentEvent) => (
                  <div
                    className={css.eventDetails}
                    key={`${tournamentEvent.start}-${tournamentEvent.eventTitle}`}
                  >
                    <div
                      onClick={() =>
                        setSelectedEvent(
                          selectedEvent &&
                            selectedEvent.index === tournamentEvent.index &&
                            selectedEvent.start === tournamentEvent.start
                            ? null
                            : tournamentEvent
                        )
                      }
                    >
                      <div className={css.titleWrapper}>
                        <div>{moment(tournamentEvent.start).format("HH:mm")}</div>
                        <div>{tournamentEvent.eventTitle}</div>
                      </div>
                    </div>
                    <div className={css.eventDetails}>
                      {selectedEvent?.index === tournamentEvent?.index && selectedEvent?.start === tournamentEvent?.start ? <CustomEvent event={selectedEvent} /> : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BookingCalendar;
